import {
    ScheduleSubModes,
    isAccruedScheduleMode,
} from "@common/helpers/domains/accounting/accruedScheduleMode";
import { fromMonetaryToInt } from "@common/helpers/monetary";
import {
    ScheduleApplicationStatus,
    ScheduleBillingStatus,
    ScheduleSourceType,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

export const SCHEDULE_BILLING_INELIGIBLE_MESSAGE =
    "Only manually applied accrued schedules can be marked as billed.";
export const SCHEDULE_UNBILLING_INELIGIBLE_MESSAGE =
    "Only manually applied accrued schedules can be marked as unbilled.";
export const SCHEDULE_NOTHING_TO_BILL_MESSAGE = "Nothing to bill on this date.";
export const SCHEDULE_CHANGE_BELOW_BILLED_MESSAGE =
    "Amount is below what's already billed. Mark the schedule as unbilled first.";
export const SCHEDULE_STOP_REMOVES_BILLED_MESSAGE =
    "This removes recognition that's already billed. Mark the schedule as unbilled first.";

const NON_BILLABLE_STATUSES: ReadonlySet<ScheduleApplicationStatus> = new Set([
    ScheduleApplicationStatus.draft,
    ScheduleApplicationStatus.unwound,
    ScheduleApplicationStatus.deleted,
    ScheduleApplicationStatus.disposed,
]);

export interface ScheduleBillingEligibilityInput extends ScheduleSubModes {
    scheduleType: ScheduleType;
    sourceType: ScheduleSourceType;
    status: ScheduleApplicationStatus;
    amount: number;
    billedAmount: number;
}

/**
 * Invoice, bill and transaction sources are the billing themselves. Manual ones are
 * billed through their billings; the lifecycle states without live entries
 * carry none, even when reverse-mode delete kept their billing rows.
 */
export function getScheduleBillingStatus(
    schedule: ScheduleBillingEligibilityInput,
): ScheduleBillingStatus | null {
    if (
        !isAccruedScheduleMode(schedule) ||
        NON_BILLABLE_STATUSES.has(schedule.status)
    ) {
        return null;
    }
    if (schedule.sourceType !== ScheduleSourceType.manual) {
        return ScheduleBillingStatus.billed;
    }
    const billedCents = fromMonetaryToInt(schedule.billedAmount);
    if (billedCents <= 0) {
        return ScheduleBillingStatus.unbilled;
    }
    return billedCents < fromMonetaryToInt(schedule.amount)
        ? ScheduleBillingStatus.partiallyBilled
        : ScheduleBillingStatus.billed;
}

export function isBillableAccruedSchedule(
    schedule: ScheduleBillingEligibilityInput,
): boolean {
    return (
        schedule.sourceType === ScheduleSourceType.manual &&
        getScheduleBillingStatus(schedule) !== null
    );
}

/** Unbilled or partially billed. */
export function canMarkScheduleBilled(
    schedule: ScheduleBillingEligibilityInput,
): boolean {
    const billingStatus = getScheduleBillingStatus(schedule);
    return (
        schedule.sourceType === ScheduleSourceType.manual &&
        (billingStatus === ScheduleBillingStatus.unbilled ||
            billingStatus === ScheduleBillingStatus.partiallyBilled)
    );
}

/** Partially billed or billed. */
export function canMarkScheduleUnbilled(
    schedule: ScheduleBillingEligibilityInput,
): boolean {
    const billingStatus = getScheduleBillingStatus(schedule);
    return (
        schedule.sourceType === ScheduleSourceType.manual &&
        (billingStatus === ScheduleBillingStatus.partiallyBilled ||
            billingStatus === ScheduleBillingStatus.billed)
    );
}
